import '@fontsource-variable/bricolage-grotesque/opsz.css';
import '@fontsource-variable/caveat';
import '@fontsource-variable/figtree';
import '@fontsource-variable/jetbrains-mono';
import './styles/global.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { legacyHashPath } from './app/navigation';
import { routes } from './app/routes';
import { audio } from './core/audio';
import { debugEnabled, exposeDebug } from './core/debug';
import { detectPitch } from './core/dsp/pitch';
import { persist } from './core/store';
import { watchTheme } from './core/theme';
import { freqToMidi } from './core/theory';
import { routeIndex } from './features/routeIndex';

// Links from the single-file version used hash routes (#gitar.egz.1).
const legacy = legacyHashPath(location.hash);
if (legacy && location.pathname === '/') history.replaceState(null, '', legacy);

document.documentElement.lang = 'tr';
watchTheme();

const router = createBrowserRouter(routes);

// Stop app audio before the next page renders.
let lastPath = router.state.location.pathname;
router.subscribe((state) => {
  if (state.location.pathname !== lastPath) {
    lastPath = state.location.pathname;
    audio.stopAll();
  }
});

// Settings are saved with a short delay; save right away when the page is hidden or closed.
window.addEventListener('pagehide', () => persist.flush());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') persist.flush();
});

// Browsers only start audio after a user gesture; unlock it on the first one.
const unlock = () => {
  audio.resume();
  window.removeEventListener('pointerdown', unlock);
  window.removeEventListener('keydown', unlock);
};
window.addEventListener('pointerdown', unlock);
window.addEventListener('keydown', unlock);

if (debugEnabled()) {
  exposeDebug('routes', routeIndex());
  exposeDebug('navigate', (path: string) => router.navigate(path));
  // What the app itself plays right now: pitch of the speaker output, for tests of play buttons.
  exposeDebug('outputPitch', () => {
    const tap = audio.outputAnalyser();
    const buf = new Float32Array(tap.fftSize);
    tap.getFloatTimeDomainData(buf);
    const p = detectPitch(buf, tap.context.sampleRate);
    return { t: tap.context.currentTime, midi: p.freq ? freqToMidi(p.freq) : null, clarity: p.clarity, rms: p.rms };
  });
}

const app = document.getElementById('app');
if (!app) throw new Error('#app bulunamadı');
createRoot(app).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
